import { ISOCountryCode, parseCountryCode } from '@foundation/countries';
import { SupportedLanguage, SupportedLocale } from '@foundation/localization';
import { parseSupportedLanguage } from '@foundation/localization/SupportedLanguageHelper';

export const LanguageUtils = {
  language: (lng: string | undefined): SupportedLanguage | undefined => {
    return parseSupportedLanguage(lng);
  },

  toLanguage: (lng: SupportedLocale): SupportedLanguage => {
    return toSupportedLanguage(lng);
  },

  locale: (lng: string | undefined): SupportedLocale | undefined => {
    const language = parseSupportedLanguage(lng);
    if (language) {
      return toSupportedLocale(language);
    }

    return undefined;
  },

  toLocale: (lng: SupportedLanguage): SupportedLocale => {
    return toSupportedLocale(lng);
  },

  country: (language: SupportedLanguage): ISOCountryCode => {
    const locale = toSupportedLocale(language);

    // Extract country code from locale
    const countryCode = locale.split('-').slice(-1).join('-'); // Get last part for language-region format

    return parseCountryCode(countryCode);
  },

  name(
    language: SupportedLanguage | SupportedLocale,
    displayLanguage?: SupportedLanguage | SupportedLocale,
  ): string {
    // Extract country from language if exists to keep only language
    // Remove only the last part to support both language-region and
    // language-script-region formats, e.g. zh-Hans-CN
    const languageWithoutRegion =
      language.split('-').slice(0, -1).join('-') || language;

    const displayLanguageWithoutRegion =
      (displayLanguage ?? language).split('-').slice(0, -1).join('-') ||
      language;

    return (
      new Intl.DisplayNames([displayLanguageWithoutRegion], {
        type: 'language',
      }).of(languageWithoutRegion) ?? languageWithoutRegion
    );
  },
};

function toSupportedLocale(language: SupportedLanguage): SupportedLocale {
  const supportedLanguages: Record<SupportedLanguage, SupportedLocale> = {
    [SupportedLanguage.Bulgarian]: SupportedLocale.Bulgarian,
    [SupportedLanguage.ChineseSimplifiedChina]:
      SupportedLocale.ChineseSimplifiedChina,
    [SupportedLanguage.ChineseTraditionalHongKong]:
      SupportedLocale.ChineseTraditionalHongKong,
    [SupportedLanguage.ChineseTraditionalTaiwan]:
      SupportedLocale.ChineseTraditionalTaiwan,
    [SupportedLanguage.Czech]: SupportedLocale.Czech,
    [SupportedLanguage.Danish]: SupportedLocale.Danish,
    [SupportedLanguage.Dutch]: SupportedLocale.Dutch,
    [SupportedLanguage.English]: SupportedLocale.English,
    [SupportedLanguage.Estonian]: SupportedLocale.Estonian,
    [SupportedLanguage.Finnish]: SupportedLocale.Finnish,
    [SupportedLanguage.French]: SupportedLocale.French,
    [SupportedLanguage.German]: SupportedLocale.German,
    [SupportedLanguage.Hungarian]: SupportedLocale.Hungarian,
    [SupportedLanguage.Icelandic]: SupportedLocale.Icelandic,
    [SupportedLanguage.Indonesian]: SupportedLocale.Indonesian,
    [SupportedLanguage.Italian]: SupportedLocale.Italian,
    [SupportedLanguage.Japanese]: SupportedLocale.Japanese,
    [SupportedLanguage.Korean]: SupportedLocale.Korean,
    [SupportedLanguage.Lithuanian]: SupportedLocale.Lithuanian,
    [SupportedLanguage.Norwegian]: SupportedLocale.Norwegian,
    [SupportedLanguage.Polish]: SupportedLocale.Polish,
    [SupportedLanguage.Portuguese]: SupportedLocale.Portuguese,
    [SupportedLanguage.Romanian]: SupportedLocale.Romanian,
    [SupportedLanguage.Russian]: SupportedLocale.Russian,
    [SupportedLanguage.Spanish]: SupportedLocale.Spanish,
    [SupportedLanguage.Swedish]: SupportedLocale.Swedish,
    [SupportedLanguage.Turkish]: SupportedLocale.Turkish,
    [SupportedLanguage.Thai]: SupportedLocale.Thai,
    [SupportedLanguage.Ukrainian]: SupportedLocale.Ukrainian,
    [SupportedLanguage.Vietnamese]: SupportedLocale.Vietnamese,
    [SupportedLanguage.Malaysian]: SupportedLocale.Malaysian,
    [SupportedLanguage.Latvian]: SupportedLocale.Latvian,
  };

  return supportedLanguages[language];
}

function toSupportedLanguage(language: SupportedLocale): SupportedLanguage {
  const supportedLanguages: Record<SupportedLocale, SupportedLanguage> = {
    [SupportedLocale.Bulgarian]: SupportedLanguage.Bulgarian,
    [SupportedLocale.ChineseSimplifiedChina]:
      SupportedLanguage.ChineseSimplifiedChina,
    [SupportedLocale.ChineseTraditionalHongKong]:
      SupportedLanguage.ChineseTraditionalHongKong,
    [SupportedLocale.ChineseTraditionalTaiwan]:
      SupportedLanguage.ChineseTraditionalTaiwan,
    [SupportedLocale.Czech]: SupportedLanguage.Czech,
    [SupportedLocale.Danish]: SupportedLanguage.Danish,
    [SupportedLocale.Dutch]: SupportedLanguage.Dutch,
    [SupportedLocale.English]: SupportedLanguage.English,
    [SupportedLocale.Estonian]: SupportedLanguage.Estonian,
    [SupportedLocale.Finnish]: SupportedLanguage.Finnish,
    [SupportedLocale.French]: SupportedLanguage.French,
    [SupportedLocale.German]: SupportedLanguage.German,
    [SupportedLocale.Hungarian]: SupportedLanguage.Hungarian,
    [SupportedLocale.Icelandic]: SupportedLanguage.Icelandic,
    [SupportedLocale.Indonesian]: SupportedLanguage.Indonesian,
    [SupportedLocale.Italian]: SupportedLanguage.Italian,
    [SupportedLocale.Japanese]: SupportedLanguage.Japanese,
    [SupportedLocale.Korean]: SupportedLanguage.Korean,
    [SupportedLocale.Lithuanian]: SupportedLanguage.Lithuanian,
    [SupportedLocale.Norwegian]: SupportedLanguage.Norwegian,
    [SupportedLocale.Polish]: SupportedLanguage.Polish,
    [SupportedLocale.Portuguese]: SupportedLanguage.Portuguese,
    [SupportedLocale.Romanian]: SupportedLanguage.Romanian,
    [SupportedLocale.Russian]: SupportedLanguage.Russian,
    [SupportedLocale.Spanish]: SupportedLanguage.Spanish,
    [SupportedLocale.Swedish]: SupportedLanguage.Swedish,
    [SupportedLocale.Turkish]: SupportedLanguage.Turkish,
    [SupportedLocale.Thai]: SupportedLanguage.Thai,
    [SupportedLocale.Ukrainian]: SupportedLanguage.Ukrainian,
    [SupportedLocale.Vietnamese]: SupportedLanguage.Vietnamese,
    [SupportedLocale.Malaysian]: SupportedLanguage.Malaysian,
    [SupportedLocale.Latvian]: SupportedLanguage.Latvian,
  };

  return supportedLanguages[language];
}
